import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { checkInService } from '../../services/checkInService';
import type { CheckIn } from '../../models/checkIn.types';

/**
 * Singleton manager for real-time snapshot subscription
 */
const snapshotManager: {
  unsub: (() => void) | null;
  listeners: number;
} = { unsub: null, listeners: 0 };

/**
 * Hook for real-time check-ins query with singleton pattern
 * Subscribes to check-ins changes for a specific key result
 *
 * @param keyResultId - ID of the key result to fetch check-ins from
 * @param limit - Optional limit for number of check-ins to fetch
 * @returns Check-ins data, loading state, and error
 */
export function useCheckInsRealtimeQuery(keyResultId: string, limit?: number) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: ['checkIns', keyResultId, limit],
    queryFn: async () => {
      // Validate keyResultId
      if (!keyResultId) {
        return [];
      }

      // Try to get cached data first
      const cached = queryClient.getQueryData<CheckIn[]>(['checkIns', keyResultId, limit]);
      if (cached && cached.length > 0) return cached;

      // Fetch initial data
      return await checkInService.getByKeyResult(keyResultId, limit);
    },
    enabled: !!keyResultId, // Only run query if keyResultId exists
    staleTime: Infinity, // Always fresh due to real-time updates
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // Skip if no keyResultId
    if (!keyResultId) {
      return;
    }

    // Increment listener count
    snapshotManager.listeners += 1;

    // Setup subscription if not already active
    if (!snapshotManager.unsub) {
      snapshotManager.unsub = checkInService.subscribeToCheckIns(keyResultId, (checkIns) => {
        // Update query cache with real-time data
        queryClient.setQueryData(['checkIns', keyResultId, limit], checkIns);
      });
    }

    // Cleanup on unmount
    return () => {
      snapshotManager.listeners -= 1;

      // Unsubscribe when no more listeners
      if (snapshotManager.listeners === 0 && snapshotManager.unsub) {
        snapshotManager.unsub();
        snapshotManager.unsub = null;
      }
    };
  }, [keyResultId, limit, queryClient]);

  return {
    checkIns: queryResult.data || [],
    isLoading: queryResult.isLoading,
    error: queryResult.error,
  };
}
